'use client';

import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { ComponentProps, PropsWithChildren, ReactNode, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import TrailDetailsOverviewLink from '@alltrails/amplitude/enums/TrailDetailsOverviewLink';
import logTrailDetailsActionTapped from '@alltrails/amplitude/events/logTrailDetailsActionTapped';
import logTrailDetailsOverviewLinkClicked from '@alltrails/amplitude/events/logTrailDetailsOverviewLinkClicked';
import useUser from '@alltrails/context/hooks/useUser';
import Typography from '@alltrails/core/components/Typography';
import ChevronSmall from '@alltrails/denali/icons/ChevronSm';
import Edit from '@alltrails/denali/icons/Edit';
import LocalizationButton from '@alltrails/layout/components/LocalizationButton';
import { Modal } from '@alltrails/modal';
import type TrailDetails from '@alltrails/types/TrailDetails';
import useTrailEditToolbar from '@/hooks/useTrailEditToolbar';
import getTrailPageType from '@/utils/getTrailPageType';
import styles from './HelpfulLink.module.scss';

const SignUpModal = dynamic(() => import('@alltrails/user-auth/components/SignUpModal'), { ssr: false });

export type HelpfulLinkAdminPanelSectionName = 'parkingFacilities' | 'gettingThere' | 'tips' | 'contactInfo';

type Props = {
  contentSource?: ReactNode;
  isBot: boolean;
  link: {
    icon: ReactNode;
    label: string;
    modalTitle?: ReactNode;
    showDirectionsButton?: boolean;
    size: ComponentProps<typeof Modal>['size'];
    adminPanelSectionName?: HelpfulLinkAdminPanelSectionName;
    id: string;
    trailDetailsOverviewLink?: TrailDetailsOverviewLink;
  };
  trailDetails: TrailDetails;
  isAdminOrPLPUser?: boolean;
};

export default function HelpfulLink({ children, contentSource, isBot, link, trailDetails, isAdminOrPLPUser }: PropsWithChildren<Props>) {
  const [isOpen, setIsOpen] = useState(false);
  const [isSignUpModalOpen, setIsSignUpModalOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [showSource, setShowSource] = useState(false);
  const user = useUser();
  const { openTrailEditToolbar } = useTrailEditToolbar();

  const latitude = trailDetails.location?.latitude;
  const longitude = trailDetails.location?.longitude;

  const url = `https://www.google.com/maps/dir/Current+Location/${latitude},${longitude}`;

  const buttons = {
    fill: false,
    primaryButton: {
      disabled: !latitude || !longitude,
      text: <FormattedMessage defaultMessage="Get directions" />,
      onClick: () => {
        logTrailDetailsActionTapped({
          trail_id: trailDetails.id.toString(),
          trail_page_action: 'get directions',
          trail_page_location: 'getting there',
          trail_page_type: getTrailPageType(trailDetails)
        });

        if (!user) {
          setIsSignUpModalOpen(true);
          return;
        }

        window.open(url, '_blank', 'noopener noreferrer');
      }
    }
  };

  const onEditIconClick = e => {
    e.stopPropagation();
    openTrailEditToolbar(link.adminPanelSectionName);
  };

  return (
    <>
      <button
        className={classNames(styles.button, { [styles.adminButton]: isAdminOrPLPUser })}
        onClick={() => {
          if (link.trailDetailsOverviewLink) {
            logTrailDetailsOverviewLinkClicked({ overview_link_clicked: link.trailDetailsOverviewLink, trail_id: trailDetails.id });
          }
          setIsOpen(true);
        }}
        type="button"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {isHovered && isAdminOrPLPUser && (
          // avoid nested buttons
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
          <div className={styles.iconDiv} onClick={onEditIconClick}>
            <div className={styles.editIcon}>
              <Edit color="--color-text-light" size="sm" />
            </div>
          </div>
        )}
        <span className={styles.icon}>{link.icon}</span>
        <Typography className={styles.label} variant="text200">
          {link.label}
        </Typography>
        <ChevronSmall className={styles.chevron} orientation="right" />
      </button>
      {isBot && (
        <Typography component="div" variant="text200">
          {children}
        </Typography>
      )}
      <Modal
        {...(link.showDirectionsButton ? { buttons } : {})}
        isOpen={isOpen}
        onCloseRequest={() => {
          setIsOpen(false);
        }}
        size={link.size}
        title={link.modalTitle ?? link.label}
      >
        <Typography className={classNames(styles.modal, { [styles.padding]: !link.showDirectionsButton })} component="div" variant="text200">
          {contentSource && showSource ? contentSource : children}
          {contentSource && (
            <LocalizationButton
              className={styles.localizationButton}
              showSource={showSource}
              toggleShowSource={() => setShowSource(showSource => !showSource)}
            />
          )}
        </Typography>
      </Modal>
      {isSignUpModalOpen && (
        <SignUpModal
          closeModal={() => setIsSignUpModalOpen(false)}
          deeplinkData={{ pageId: trailDetails.id, pageType: 'trail' }}
          isOpen={isSignUpModalOpen}
          onSuccess={() => {
            setIsSignUpModalOpen(false);
            setIsOpen(true);
          }}
          trigger="Trail Detail Directions"
          upsellTrigger="Trail Detail Directions"
        />
      )}
    </>
  );
}
